/**
 * Goal 2: report mass list_update as one dialog-owned operation.
 * Dialog owns feedback — no toast.promise for the same op.
 */
import {
  getOperationCoordinator,
  STEP_STATUS,
  OUTCOME,
  normalizeError,
  hasMeaningfulErrors,
  canConfirmStepFromEvidence,
  outcomeAfterRefreshFailure,
} from "../../../../operationFeedback";

export const LIST_UPDATE_REPORTS_OPERATION_KEY = "list_update_reports";

/**
 * @param {object} values
 * @returns {{ ready: boolean, reason: string|null, count: number }}
 */
export function evaluateListUpdateReportsPreflight(values) {
  const ids = values?.ids;
  if (!Array.isArray(ids) || ids.length === 0) {
    return { ready: false, reason: "no_selection", count: 0 };
  }
  return { ready: true, reason: null, count: ids.length };
}

function defaultExecutionSteps(t) {
  return [
    {
      key: "preflight",
      label: t("operation_feedback.list_update_reports.step.preflight"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "update",
      label: t("operation_feedback.list_update_reports.step.update"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "refresh",
      label: t("operation_feedback.list_update_reports.step.refresh"),
      status: STEP_STATUS.PENDING,
    },
  ];
}

/**
 * @param {object} args
 * @param {Function} args.dispatch
 * @param {Function} args.listUpdateAsync - RTK thunk (listUpdateReportAsync)
 * @param {object} args.values - form values including ids[]
 * @param {string|number} [args.companyId]
 * @param {Function} [args.onRefresh]
 * @param {Function} args.t
 */
export async function runListUpdateReportsOperation({
  dispatch,
  listUpdateAsync,
  values,
  companyId,
  onRefresh,
  t,
}) {
  const coordinator = getOperationCoordinator();
  const count = Array.isArray(values?.ids) ? values.ids.length : 0;
  const title = t("operation_feedback.list_update_reports.title", {
    count,
  });

  const op = coordinator.start({
    operationKey: LIST_UPDATE_REPORTS_OPERATION_KEY,
    title,
    presentationOwner: "dialog",
    resourceId: null,
    companyId,
    steps: defaultExecutionSteps(t),
  });
  const id = op.clientOperationId;
  let rev = 1;

  const preflight = evaluateListUpdateReportsPreflight(values);

  if (
    !canConfirmStepFromEvidence({
      fromReadiness: true,
      status: STEP_STATUS.COMPLETED,
    })
  ) {
    /* intentional gate */
  }

  if (!preflight.ready) {
    const detail = t(
      "operation_feedback.list_update_reports.preflight_no_selection"
    );
    coordinator.updateStep(id, "preflight", {
      status: STEP_STATUS.NEEDS_FIX,
      detail,
      revision: rev++,
    });
    coordinator.updateStep(id, "update", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: detail,
      outcome: OUTCOME.NOT_APPLIED,
    });
    return { clientOperationId: id, ok: false, error: { kind: "preflight" } };
  }

  coordinator.updateStep(id, "preflight", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.list_update_reports.preflight_ok", {
      count: preflight.count,
    }),
    revision: rev++,
  });

  coordinator.updateStep(id, "update", {
    status: STEP_STATUS.RUNNING,
    revision: rev++,
  });

  let action;
  try {
    action = await dispatch(listUpdateAsync(values));
  } catch (err) {
    const normalized = normalizeError(err, {
      operationKey: LIST_UPDATE_REPORTS_OPERATION_KEY,
    });
    coordinator.updateStep(id, "update", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized };
  }

  const payload = action?.payload;
  const rejected = action?.meta?.requestStatus === "rejected";

  if (
    rejected ||
    payload?.isAxiosError ||
    hasMeaningfulErrors(payload?.errors) ||
    payload?.error === true
  ) {
    const normalized = normalizeError(
      rejected
        ? { response: { status: payload?.response?.status, data: payload } }
        : payload?.response
          ? payload
          : { response: { status: 422, data: payload } },
      { operationKey: LIST_UPDATE_REPORTS_OPERATION_KEY }
    );
    coordinator.updateStep(id, "update", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized, payload };
  }

  coordinator.updateStep(id, "update", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.list_update_reports.update_ok", {
      count: preflight.count,
    }),
    resultRef: { count: preflight.count },
    revision: rev++,
  });

  if (typeof onRefresh === "function") {
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.RUNNING,
      revision: rev++,
    });
    try {
      await Promise.resolve(onRefresh());
      coordinator.updateStep(id, "refresh", {
        status: STEP_STATUS.COMPLETED,
        revision: rev++,
      });
      const afterOk = outcomeAfterRefreshFailure(STEP_STATUS.COMPLETED, false);
      coordinator.complete(id, {
        summary: t("operation_feedback.list_update_reports.success", {
          count: preflight.count,
        }),
        outcome: afterOk.outcome || OUTCOME.APPLIED,
      });
      return { clientOperationId: id, ok: true, payload };
    } catch (refreshErr) {
      const normalized = normalizeError(refreshErr, {
        operationKey: LIST_UPDATE_REPORTS_OPERATION_KEY,
      });
      const afterFail = outcomeAfterRefreshFailure(STEP_STATUS.COMPLETED, true);
      coordinator.updateStep(id, "refresh", {
        status: afterFail.refreshStatus || STEP_STATUS.FAILED,
        detail: normalized.safeMessage,
        revision: rev++,
      });
      coordinator.complete(id, {
        summary: t(
          afterFail.summaryKey || "operation_feedback.saved_list_refresh_failed"
        ),
        outcome: afterFail.outcome || OUTCOME.PARTIAL,
      });
      return { clientOperationId: id, ok: true, payload, refreshFailed: true };
    }
  }

  coordinator.updateStep(id, "refresh", {
    status: STEP_STATUS.NOT_RELEVANT,
    revision: rev++,
  });
  coordinator.complete(id, {
    summary: t("operation_feedback.list_update_reports.success", {
      count: preflight.count,
    }),
    outcome: OUTCOME.APPLIED,
  });
  return { clientOperationId: id, ok: true, payload };
}
